import { useMemo, useEffect } from 'react';

import styles from './styles.module.css';
import {
  EnhancedPaginationProps,
  usePaginationTokens
} from './usePaginationTokens';
import PaginationArrowIcon from '@/assets/icons/pagination-arrow.svg?react';
import PaginationDotsIcon from '@/assets/icons/pagination-dots.svg?react';

/**
 * Reusable Pagination Component with Token Support
 *
 * Features:
 * - Independent from any table or data component
 * - Previous/Next navigation arrows
 * - Token-based navigation for API endpoints
 * - Stores tokens for backward navigation
 * - Accessible keyboard navigation
 *
 * @param props - EnhancedPaginationProps
 * @returns JSX.Element
 */
export default function Pagination({
  currentPage,
  totalPages,
  pageSize,
  nextPageToken,
  onPageChange,
  maxVisiblePages = 5,
  showArrows = true,
  className = '',
  useTokens = false
}: EnhancedPaginationProps) {
  const tokenManager = usePaginationTokens();

  // Store tokens when they arrive from API
  useEffect(() => {
    if (useTokens && nextPageToken && currentPage < totalPages) {
      tokenManager.setToken(currentPage + 1, nextPageToken);
    }
  }, [
    nextPageToken,
    currentPage,
    totalPages,
    useTokens,
    tokenManager.setToken
  ]);
  const visiblePages = useMemo(() => {
    const safeTotal = Math.max(0, totalPages);
    if (safeTotal <= maxVisiblePages) {
      return Array.from({ length: safeTotal }, (_, i) => i + 1);
    }

    const pages: (number | 'ellipsis')[] = [];
    const halfVisible = Math.floor(maxVisiblePages / 2);

    pages.push(1);

    if (currentPage <= halfVisible + 2) {
      for (let i = 2; i <= Math.min(maxVisiblePages - 1, totalPages - 1); i++) {
        pages.push(i);
      }
      if (totalPages > maxVisiblePages - 1) {
        pages.push('ellipsis');
      }
    } else if (currentPage >= totalPages - halfVisible - 1) {
      if (totalPages > maxVisiblePages - 1) {
        pages.push('ellipsis');
      }
      for (
        let i = Math.max(2, totalPages - maxVisiblePages + 2);
        i <= totalPages - 1;
        i++
      ) {
        pages.push(i);
      }
    } else {
      pages.push('ellipsis');
      for (
        let i = currentPage - halfVisible + 1;
        i <= currentPage + halfVisible - 1;
        i++
      ) {
        pages.push(i);
      }
      pages.push('ellipsis');
    }

    if (safeTotal > 1) {
      pages.push(safeTotal);
    }

    return pages;
  }, [currentPage, totalPages, maxVisiblePages]);

  const handlePageClick = (page: number) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      if (useTokens) {
        const direction =
          page > currentPage
            ? 'next'
            : page < currentPage
              ? 'previous'
              : 'jump';
        const params = tokenManager.getNavigationParams(
          page,
          pageSize,
          currentPage,
          direction
        );
        onPageChange(params);
      } else {
        // For non-token pagination, use simple page number
        onPageChange({
          page,
          pageSize,
          pageNumber: page,
          method: 'pageNumber',
          direction: 'jump'
        });
      }
    }
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      const prevPage = currentPage - 1;
      if (useTokens) {
        const params = tokenManager.getNavigationParams(
          prevPage,
          pageSize,
          currentPage,
          'previous'
        );
        onPageChange(params);
      } else {
        onPageChange({
          page: prevPage,
          pageSize,
          pageNumber: prevPage,
          method: 'pageNumber',
          direction: 'previous'
        });
      }
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      const nextPage = currentPage + 1;
      if (useTokens) {
        const params = tokenManager.getNavigationParams(
          nextPage,
          pageSize,
          currentPage,
          'next'
        );
        onPageChange(params);
      } else {
        onPageChange({
          page: nextPage,
          pageSize,
          pageNumber: nextPage,
          method: 'pageNumber',
          direction: 'next'
        });
      }
    }
  };

  if (totalPages < 1) {
    return null;
  }

  return (
    <div className={`${styles.paginationContainer} ${className}`}>
      {showArrows && (
        <button
          className={`${styles.paginationButton} ${styles.arrowButton} ${styles.leftArrow} ${
            currentPage === 1 ? styles.disabled : ''
          }`}
          onClick={handlePrevious}
          disabled={currentPage === 1}
          aria-label="Previous page"
        >
          <PaginationArrowIcon className={styles.arrowIcon} />
        </button>
      )}

      {visiblePages.map((page, index) => {
        if (page === 'ellipsis') {
          return (
            <span key={`ellipsis-${index}`} className={styles.ellipsis}>
              <PaginationDotsIcon className={styles.dotsIcon} />
            </span>
          );
        }

        return (
          <button
            key={page}
            className={`${styles.paginationButton} ${styles.pageButton} ${
              page === currentPage ? styles.active : ''
            }`}
            onClick={() => handlePageClick(page)}
            aria-label={`Go to page ${page}`}
            aria-current={page === currentPage ? 'page' : undefined}
          >
            {page}
          </button>
        );
      })}

      {showArrows && (
        <button
          className={`${styles.paginationButton} ${styles.arrowButton} ${styles.rightArrow} ${
            currentPage === totalPages ? styles.disabled : ''
          }`}
          onClick={handleNext}
          disabled={currentPage === totalPages}
          aria-label="Next page"
        >
          <PaginationArrowIcon className={styles.arrowIcon} />
        </button>
      )}
    </div>
  );
}
